import React, { useEffect, MouseEvent } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { State } from '@meraki-internal/state';
import { HistoryViewModel } from './support/HistoryViewModel';
import { mediaMinWidth } from './theme/utils';
import { SIDE_NAV_WIDTH } from './navigation/NavConstants';

const useStyles = createUseStyles({
    // Starts the first tab where the page's content column starts. Left/right padding matches the
    // content padding in InnerhivePage.
    columnBand: {
        paddingLeft: 'var(--ion-padding, 16px)',
        paddingRight: 'var(--ion-padding, 16px)',
        [mediaMinWidth.md]: {
            paddingLeft: '15%',
            paddingRight: '15%'
        },
        [mediaMinWidth.lg]: {
            paddingLeft: `calc(${SIDE_NAV_WIDTH}px + 20%)`,
            paddingRight: '20%'
        }
    },

    tabs: {
        display: 'flex',
        gap: 2
    },

    tab: {
        display: 'block',
        padding: '9px 16px',
        borderBottom: '2.5px solid transparent',
        fontSize: 14,
        color: 'rgba(255, 255, 255, 0.66)',
        textDecoration: 'none',
        whiteSpace: 'nowrap'
    },

    tabOn: {
        color: '#fff',
        fontWeight: 500,
        borderBottomColor: 'var(--ion-color-secondary)'
    }
});

export interface IHeaderTabGroup {
    tabGroupId: string;
    tabs: IHeaderTab[];
}

export interface IHeaderTab {
    label: string;
    url: string;
    dataId?: string;
    onClick?: () => void;
}

const Link: React.FC<IHeaderTab & { selected: boolean }> = ({ url, label, selected, dataId, onClick }) => {
    const classes = useStyles();
    const blankUrl = '';
    const history = useInstance(HistoryViewModel);
    return (
        <div data-id={dataId} onClick={onClick}>
            <a
                className={`${classes.tab} ${selected ? classes.tabOn : ''}`}
                href={blankUrl}
                aria-current={selected ? 'page' : undefined}
                onClick={(e: MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    history.push(url);
                }}
            >{label}</a>
        </div>
    );
};

export class HeaderTabsState extends State {
    setLastActiveTab = (tabGroupId: string, tab: IHeaderTab) => {
        this.setState({[tabGroupId]: tab});
    };
    getLastActiveTab = (tabGroupId: string) => {
        return this.state?.[tabGroupId] || undefined;
    };
}

// TODO: convert to MDTabs at some point

export const HeaderTabs: React.FC<{
    tabs: IHeaderTabGroup;
}> = ({ tabs }) => {
    const classes = useStyles();
    const history = useInstance(HistoryViewModel);
    const tabsState = useInstance(HeaderTabsState);

    const pathname = history.getCurrentLocation().pathname;
    const selectedTab = tabs.tabs.find(t => pathname.startsWith(t.url));

    useEffect(() => {
        if (selectedTab) {
            tabsState.setLastActiveTab(tabs.tabGroupId, selectedTab);
        }
    }, [tabs]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className={classes.columnBand}>
            <div className={classes.tabs}>
                {tabs.tabs.map(tab => (
                    <Link
                        key={tab.label}
                        url={tab.url}
                        label={tab.label}
                        onClick={tab.onClick}
                        selected={tab === selectedTab} dataId={tab.dataId}
                    />
                ))}
            </div>
        </div>
    );
};
